import { Box, Flex, For, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { MdOutlineStar, MdOutlineStarBorder } from 'react-icons/md';
import FaceliftButton from '@/components/Atoms/FaceliftButton/FaceliftButton';
import FaceliftCheckbox from '@/components/Atoms/FaceliftCheckbox';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import { FaceliftSquaresLoadingIndicator } from '@/components/Atoms/FaceliftSquaresLoadingIndicator.tsx';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import { Tooltip } from '@/components/ui/tooltip';
import { useGetBvProducts } from '@/hooks/service-hooks/products';
import { useUpdateUserMsProducts } from '@/hooks/service-hooks/users';
import { useAuthStore, useClinicStore } from '@/store';

type ProductType = {
	id: number;
	label: string;
	value: string;
	checked: boolean;
	starred: boolean;
};

const Products = () => {
	const clinics = useClinicStore((state) => state.currentClinics);
	const user = useAuthStore((state) => state.user?.userInformation);
	const userCommissionableProducts = user?.commissionableProducts || [];

	const [mSproductsState, setMSProductsState] = useState<ProductType[]>([]);
	const [nonMSproductsState, setNonMSProductsState] = useState<ProductType[]>(
		[],
	);

	const { mutate: updateMsProducts, isLoading } = useUpdateUserMsProducts(
		user?._id as string,
	);

	const { data: bvProducts, isFetching } = useGetBvProducts({
		clinicId: '-',
		clinics: clinics?.map((c) => c.accountid).join(',') || '',
		userPort: user?.portsAvailable || 'WoundCare+',
		enabled: true,
	});

	useEffect(() => {
		if (bvProducts?.items) {
			const allProducts = Array.from(
				new Map(
					bvProducts?.items
						.flatMap((el) => el.accountBvProducts)
						.map((p) => [p.qCode, p]),
				).values(),
			).map((p, index) => ({
				id: index,
				label: p.text,
				value: p.qCode,
				checked: userCommissionableProducts.some(
					(prod) => prod.qCode === p.qCode && prod.checked,
				),
				starred: userCommissionableProducts.some(
					(prod) => prod.qCode === p.qCode && prod.starred,
				),
			}));
			setMSProductsState(allProducts);
		}
	}, [bvProducts]);

	const handleMSProductCheckboxChange = (id: number, value: boolean) => {
		setMSProductsState((prev) =>
			prev.map((p) => (p.id === id ? { ...p, checked: value } : p)),
		);
	};

	const handleMSProductStarToggle = (id: number) => {
		setMSProductsState((prev) =>
			prev.map((p) => (p.id === id ? { ...p, starred: !p.starred } : p)),
		);
	};

	const handleNonMSProductCheckboxChange = (id: number, value: boolean) => {
		setNonMSProductsState((prev) =>
			prev.map((p) => (p.id === id ? { ...p, checked: value } : p)),
		);
	};

	const handleNonMSProductStarToggle = (id: number) => {
		setNonMSProductsState((prev) =>
			prev.map((p) => (p.id === id ? { ...p, starred: !p.starred } : p)),
		);
	};

	const handleSubmitMSProducts = () => {
		const authStore = useAuthStore.getState();
		authStore.updateMsProducts(
			mSproductsState
				.filter((el) => el.checked || el.starred)
				.map((p) => ({
					qCode: p.value,
					checked: p.checked,
					starred: p.starred,
				})),
		);
		updateMsProducts(
			mSproductsState
				.filter((el) => el.checked || el.starred)
				.map((p) => ({
					qCode: p.value,
					checked: p.checked,
					starred: p.starred,
				})),
		);
	};

	return (
		<>
			<Box w="full" mb={8}>
				<FaceliftText fontStyle="italic" fontWeight="bold" mb={3}>
					Total Ancillary Products
				</FaceliftText>

				<Show when={isFetching}>
					<VStack justifyContent={'center'}>
						<FaceliftSquaresLoadingIndicator />
						<FaceliftText>Loading Products...</FaceliftText>
					</VStack>
				</Show>

				<VStack align="stretch">
					<For each={mSproductsState}>
						{(product) => (
							<Flex
								key={product.id}
								align="center"
								justify="space-between"
								borderBottom="1px solid"
								borderColor="gray.200"
							>
								<FaceliftCheckbox
									checked={product.checked}
									onCheckedChange={(checked: boolean) =>
										handleMSProductCheckboxChange(product.id, !!checked)
									}
									label={product.label}
									size="sm"
								/>
								<FaceliftIconButton
									bg="transparent"
									variant={product.starred ? 'solid' : 'ghost'}
									aria-label="star"
									size="xs"
									onClick={() => handleMSProductStarToggle(product.id)}
									_hover={{
										color: product.starred ? 'brand.warning' : 'brand.solid',
										bg: 'gray.100',
									}}
									color={product.starred ? 'brand.warning' : 'inherit'}
								>
									<Tooltip 
										content={
											product.starred
												? 'Remove from favorites'
												: 'Add to favorites'
										} 
										positioning={{ placement: 'top-center' }} showArrow>
											<Show when={product.starred}>
												<MdOutlineStar />
											</Show>
											<Show when={!product.starred}>
												<MdOutlineStarBorder />
											</Show>
									</Tooltip>
								</FaceliftIconButton>
							</Flex>
						)}
					</For>
				</VStack>

				<Show when={!isFetching}>
					<Flex justify="flex-end" mt={4}>
						<FaceliftButton
							width="250px"
							size="md"
							onClick={handleSubmitMSProducts}
							loading={isLoading}
						>
							Assign Product
						</FaceliftButton>
					</Flex>
				</Show>
			</Box>

			{/* <Box w="full">
				<FaceliftText fontStyle="italic" fontWeight="bold" mb={3}>
					Clinic Products
				</FaceliftText>

				<VStack align="stretch">
					<For each={nonMSproductsState}>
						{(product) => (
							<Flex
								key={product.id}
								align="center"
								justify="space-between"
								borderBottom="1px solid"
								borderColor="gray.200"
							>
								<FaceliftCheckbox
									checked={product.checked}
									onCheckedChange={(checked: boolean) =>
										handleNonMSProductCheckboxChange(product.id, !!checked)
									}
									label={product.label}
									size="sm"
								/>

								<FaceliftIconButton
									bg="transparent"
									variant={product.starred ? 'solid' : 'ghost'}
									aria-label="star"
									size="xs"
									onClick={() => handleNonMSProductStarToggle(product.id)}
									_hover={{
										color: product.starred ? 'brand.warning' : 'brand.solid',
										bg: 'gray.100',
									}}
									color={product.starred ? 'brand.warning' : 'inherit'}
								>
									{product.starred ? (
										<MdOutlineStar />
									) : (
										<MdOutlineStarBorder />
									)}
								</FaceliftIconButton>
							</Flex>
						)}
					</For>
				</VStack>

				<Flex justify="flex-end" mt={4}>
					<FaceliftButton width="250px" size="md">
						Assign Product
					</FaceliftButton>
				</Flex>
			</Box> */}
		</>
	);
};

export default Products;
